// @owners { team: patient-fulfillment }
import { useCallback, useMemo } from 'react';
import { getNextDeliveryForPrescriptionID } from '~shared/features/delivery/selectors/getNextDeliveryForPrescriptionID';
import { getInstructionalVideosForMedication } from '~shared/features/my-meds/helper';
import { makeGetMedicationTagKey } from '~shared/features/my-meds/selectors/getMedicationTagKey';
import { getPartitionedPrescriptionsForMedication } from '~shared/features/my-meds/selectors/getPartitionedPrescriptionsByMedication';
import { type Medication } from '~shared/features/my-meds/types';
import { useNextAvailableDatesForPrescriptions } from '~shared/features/next-available-date/queries/useNextAvailableDatesForPrescriptions';
import { getIsScheduleableByPrescriptionID } from '~shared/features/prescriptions/selectors/getScheduleableForPrescriptionID';
import { PRESCRIPTION_PRICING_LABELS } from '~shared/features/pricing/constants';
import {
  type PriceInfo,
  getPricingInfoForPrescription,
} from '~shared/features/pricing/selectors/getPricingInfoForPrescription';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useSelectorShared } from '~shared/store';

const AVAILABLE = 'available';

type FirstScheduleableRxProperties = {
  availableForNextDeliveryWindow: boolean | null | undefined;
  delayedAvailabilityReason: string | null | undefined;
  id: number | null | undefined;
  nextAvailableDateFromNow: string | null | undefined;
  priceShown: string | null | undefined;
  priceType: string | null | undefined;
};

const getPriceType = (totalReason?: PriceInfo['totalReason']) => {
  switch (totalReason) {
    case PRESCRIPTION_PRICING_LABELS.YOUR_COPAY:
      return 'certain';
    case PRESCRIPTION_PRICING_LABELS.LAST_COPAY:
      return 'estimated';
    case PRESCRIPTION_PRICING_LABELS.PRICE_UNAVAILABLE:
      return 'unknown';
    default:
      return null;
  }
};

type Props = {
  medication: Medication | undefined;
  actionablePrescriptionsCount: number;
  inactivePrescriptionsCount: number;
};

export const useMedListDetailsViewed = ({
  medication,
  actionablePrescriptionsCount,
  inactivePrescriptionsCount,
}: Props) => {
  const { key } = useSelectorShared((state) => makeGetMedicationTagKey(state, { medication }));
  const hasVideos = !!getInstructionalVideosForMedication(medication).length;
  const { actionablePrescriptions } = useSelectorShared((state) =>
    getPartitionedPrescriptionsForMedication(state, { medication }),
  );
  const isScheduleableByPrescriptionID = useSelectorShared(getIsScheduleableByPrescriptionID);
  const scheduleablePrescriptions = actionablePrescriptions.filter(
    (p) => isScheduleableByPrescriptionID[p.id] || false,
  );
  const prescriptionID = scheduleablePrescriptions[0]?.id;

  const prices = useSelectorShared((state) =>
    prescriptionID ? getPricingInfoForPrescription(state, prescriptionID) : null,
  );

  const nadParams = prescriptionID ? { prescriptionID } : {};
  const { nextAvailableDate } = useNextAvailableDatesForPrescriptions(nadParams);
  const earliestAvailableDate = nextAvailableDate?.earliest.date;
  const earliestAvailableReason = nextAvailableDate?.earliest.reason;

  const { trackEvent } = useAnalytics();

  const eventProperties = useMemo(() => {
    const properties: FirstScheduleableRxProperties = {
      id: prescriptionID,
      availableForNextDeliveryWindow: null,
      delayedAvailabilityReason: null,
      nextAvailableDateFromNow: null,
      priceShown: null,
      priceType: null,
    };

    if (prices?.length === 1) {
      const { price, totalReason } = prices[0];
      properties.priceShown = price;
      properties.priceType = getPriceType(totalReason);
    }

    if (earliestAvailableDate && earliestAvailableReason) {
      properties.availableForNextDeliveryWindow = earliestAvailableReason === AVAILABLE;
      properties.delayedAvailabilityReason = earliestAvailableReason;
      properties.nextAvailableDateFromNow = earliestAvailableDate;
    }
    return properties;
  }, [prices, earliestAvailableDate, earliestAvailableReason, prescriptionID]);

  const delivery = useSelectorShared((state) =>
    prescriptionID
      ? getNextDeliveryForPrescriptionID(state, {
          prescriptionID,
        })
      : null,
  );

  const createMedListDetailsViewedAnalyticsEvent = useCallback(
    (origin: string) => {
      const properties = {
        state: key,
        prescriptionId: eventProperties.id,
        deliveryId: delivery?.id,
        origin,
        medication_key: medication?.key ?? 'unknown',
        medication_name: medication?.medicationName ?? 'unknown',
        'number active rx': scheduleablePrescriptions.length,
        'price shown': eventProperties.priceShown,
        'price type': eventProperties.priceType,
        'next available date from now': eventProperties.nextAvailableDateFromNow,
        'available for next delivery window': eventProperties.availableForNextDeliveryWindow,
        'delayed availability reason': eventProperties.delayedAvailabilityReason,
        'has videos': hasVideos,
        'actionable rx count': actionablePrescriptionsCount,
        'inactive rx count': inactivePrescriptionsCount,
      };
      trackEvent({ event: EVENTS.MED_DETAILS__VIEWED, params: properties });
    },
    [
      eventProperties,
      scheduleablePrescriptions,
      delivery,
      hasVideos,
      key,
      medication,
      actionablePrescriptionsCount,
      inactivePrescriptionsCount,
      trackEvent,
    ],
  );

  return {
    createMedListDetailsViewedAnalyticsEvent,
  };
};
